import { Image, LineClamp } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import Link from "next/link";

import PossibleTokens from "@/challenges/PossibleTokens";
import ProgressBarTokensCounter from "@/challenges/ProgressBarTokensCounter";
import TopicMetaRow from "@/challenges/TopicMetaRow";
import { ChallengeTopic } from "@/challenges/utils/interfaces";
import ChallengesActivityIcon from "@/icons/ChallengesActivityIcon";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import {
  cardHeaderBackgroundCss,
  challengeDetailCardBadgesContentCss,
  challengeDetailCardBadgesTitleCss,
  challengeDetailCardContentCss,
  challengeDetailCardCss,
  challengeDetailCardTitle,
  challengeDetailHeaderCss,
  descriptionChallengeTextCss,
  finalScoresCss,
  metaCss,
  metaText,
  possibleTokensContainer,
  possibleTokensTeacherHome,
  topicRowCss,
} from "./ChallengeDetailCard.style";

interface ChallengeDetailCardProps {
  isHomeVersion?: boolean;
  topic: ChallengeTopic;
}

const LinkWithTrackEvent = withTrackEvent(Link);

const ChallengeDetailCard = ({
  isHomeVersion,
  topic,
}: ChallengeDetailCardProps) => {
  const { isTeacher } = useGetUserProperties();

  const topicMetaRockMock = [
    {
      total: topic.total_articles,
      label: "articles",
    },
    {
      total: topic.total_interactions,
      label: "activities",
    },
  ];

  const renderTeacherHomeDetailCard = () => {
    return (
      <div css={challengeDetailCardContentCss}>
        <div css={possibleTokensTeacherHome}>
          <PossibleTokens tokens={topic.tokens} />
        </div>
        <h4 css={challengeDetailCardTitle}>{topic.title}</h4>
        <div css={metaCss}>
          {topicMetaRockMock.map((meta, index) => {
            return (
              <p key={index} css={metaText}>
                {meta.total} {meta.label}
              </p>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDefaultDetailCard = () => {
    return (
      <>
        <div css={challengeDetailHeaderCss}>
          <Image
            src={topic.image?.url}
            __css={cardHeaderBackgroundCss}
            alt=""
            dataProps={{ "data-testid": `topic-image-${topic.topic_id}` }}
          />
          <div css={possibleTokensContainer}>
            <PossibleTokens tokens={topic.tokens} />
          </div>
        </div>
        <div css={challengeDetailCardContentCss}>
          <h4 css={challengeDetailCardTitle}>{topic.title}</h4>
          {!isHomeVersion && (
            <LineClamp
              css={descriptionChallengeTextCss}
              lines={2}
              text={topic.description}
              innerElement="p"
            />
          )}
          {isTeacher ? (
            <div css={topicRowCss}>
              <TopicMetaRow topics={topicMetaRockMock} />
            </div>
          ) : (
            <>
              <div css={challengeDetailCardBadgesContentCss}>
                <div css={challengeDetailCardBadgesTitleCss}>
                  <ChallengesActivityIcon />
                  <h5>Activities completed</h5>
                </div>
                <span css={finalScoresCss}>
                  {topic.done_interactions} / {topic.total_interactions}
                </span>
              </div>
              <ProgressBarTokensCounter
                label="Tokens earned"
                achievedTokens={topic.earned_tokens}
                totalTokens={topic.tokens}
                version="DETAILS_CARD_VERSION"
              />
            </>
          )}
        </div>
      </>
    );
  };

  return (
    <LinkWithTrackEvent
      css={challengeDetailCardCss}
      href={`/challenges/${topic.topic_id}/`}
      objectType={objectTypes.user}
      actionName={actionNames.navigate}
      streamProperties={{
        application: "parnassus",
        component_type: "ChallengeDetailCard",
        description: "Redirect to Topic Page",
      }}
      data-qa-selector={`IRC: Redirect to Topic Page with topic id ${topic.topic_id}`}
    >
      {isTeacher && isHomeVersion
        ? renderTeacherHomeDetailCard()
        : renderDefaultDetailCard()}
    </LinkWithTrackEvent>
  );
};

export default ChallengeDetailCard;
